import React, { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Helmet } from "react-helmet";
import ViewEvent from '../../components/viewEvent/viewEvent';
import ViewDriver from "../../components/viewDriver/viewDriver";
import axiosInstance from "../../api/axiosInstance";
import moment from 'moment';
import "./calendar.scss";

const Calendar = () => {
    const { category, selectedDate, viewMode, setSelectedDate, setViewMode } = useOutletContext();

    const today = new Date().toISOString().split('T')[0];

    const [items, setItems] = useState([]);
    const [teams, setTeams] = useState([]);
    const [events, setEvents] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [formModal, setFormModal] = useState(false);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedDriverModal, setSelectedDriverModal] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);

    const monday = moment(selectedDate).startOf('isoWeek');
    const selectedDay = moment(selectedDate).startOf('day');

    const days = viewMode === 'week'
        ? Array.from({ length: 5 }, (_, index) => monday.clone().add(index, 'days'))
        : [selectedDay];

    const startHourGrid = 0;
    const endHourGrid = 23;

    const hours = viewMode === 'week'
        ? Array.from({ length: 10 }, (_, index) => selectedDay.clone().isoWeekday(1).add(Math.floor(index / 2), 'days').startOf('day').toDate())
        : Array.from({ length: endHourGrid - startHourGrid + 1 }, (_, index) => selectedDay.clone().hour(startHourGrid + index).toDate());

    const fetchData = useCallback(async () => {
        try {
            const start = viewMode === 'week' ? moment(selectedDate).startOf('isoWeek').format("YYYY-MM-DD") : moment(selectedDate).format("YYYY-MM-DD");
            const end = viewMode === 'week' ? moment(selectedDate).endOf('isoWeek').format("YYYY-MM-DD") : moment(selectedDate).format("YYYY-MM-DD");

            const [itemsRes, teamsRes, eventsRes] = await Promise.all([
                axiosInstance.get(`items`, { params: { category } }),
                axiosInstance.get(`teams`, { params: { category } }),
                axiosInstance.get(`events?start=${start}&end=${end}`)
            ]);

            setItems(itemsRes.data);
            setTeams(teamsRes.data);
            setEvents(eventsRes.data);
            setLoading(false);
        } catch (err) {
            console.error(err);
        }
    }, [category, selectedDate, viewMode]);

    useEffect(() => {
        fetchData();

        const interval = setInterval(() => {
            fetchData();
        }, 30000);

        return () => clearInterval(interval);
    }, [fetchData]);

    const handleEventClick = (event) => {
        setFormModal(true);
        setSelectedEventId(event.id);
    };

    const handlePlannerClick = (item, time) => {
        setSelectedEventId(null);
        setFormModal(true);
        setSelectedItem(item)
        setSelectedTime(time)
    }

    const handleDriverClick = (item) => {
        setSelectedDriverModal(item.id);
    };

    const calculateEventPosition = (event) => {
        const start = new Date(`${event.startDate.split('T')[0]}T${event.startTime}`);
        const end = new Date(`${event.endDate.split('T')[0]}T${event.endTime}`);
        const weekStart = new Date(viewMode === 'week' ? monday : selectedDay);
        const weekEnd = new Date(viewMode === 'week' ? moment(monday).add(4, 'days').endOf('day') : moment(selectedDay).endOf('day'));

        const totalDays = viewMode === 'week' ? 5 : 1;
        const dayWidth = 100 / totalDays;

        const clampedStart = start < weekStart ? weekStart : start;
        const clampedEnd = end > weekEnd ? weekEnd : end;

        const startOffset = (clampedStart - weekStart) / (1000 * 60 * 60 * 24);
        const endOffset = (clampedEnd - weekStart) / (1000 * 60 * 60 * 24);

        const left = Math.max(0, startOffset * dayWidth);
        const width = Math.max(1, (endOffset - startOffset) * dayWidth);

        return { left: `calc(${left}% + 3px)`, width: `calc(${width}% - 10px)` };
    };

    return isLoading ? (<div className="loading" />) : (
        <div className="calendar">
            <Helmet>
                <title>Kalender</title>
            </Helmet>
            <div className="timeline">
                <div className="header">
                    <div className="left">
                        <span>Beschreibung</span>
                    </div>
                    <div className="right">
                        {viewMode === 'week' ? (
                            days.map((day, dayIndex) => (
                                <div className="item" key={dayIndex}>
                                    <div
                                        className={today === moment(day).format("YYYY-MM-DD") ? "day today" : "day"}
                                        onClick={() => {
                                            setSelectedDate(moment(day).format("YYYY-MM-DD"));
                                            setViewMode('day');
                                        }}
                                    >
                                        <span className="date">{day.format('D')}</span>
                                        <span className="weekday">{day.format('dd')}</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            hours.map((hour, hourIndex) => (
                                <div className="item" key={hourIndex}>
                                    <div className="hour" onClick={() => { setViewMode('week') }}>
                                        <span className="time">{moment(hour).format('H')}</span>
                                        <span className="minutes">{moment(hour).format('mm')}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
                <div className="content">
                    <div className="left">
                        {items.map(item => (
                            <div className="item" key={`I${item.id}`}>
                                <span className="alias" title={item.alias + " - " + item.description}>{item.alias} - {item.description}</span>
                                <span className="desc" onClick={() => handleDriverClick(item)}>
                                    {item.driverAlias === null ? "Kein Fahrer" : item.driverAlias}
                                </span>
                            </div>
                        ))}
                        {teams.map(item => (
                            <div className="item" key={`T${item.driver}`}>
                                <span className="alias">{category === 1 ? item.alias : item.driverAlias}</span>
                                <span className="desc">{(category === 1 ? item.description : item.position) + (item.alias !== null ? (" - " + item.alias) : "")}</span>
                            </div>
                        ))}
                    </div>
                    <div className="right">
                        {items.map(item => (
                            <div className="row" key={`I${item.id}`}>
                                <div className="boxes">
                                    {hours.map((hour, hourIndex) => (
                                        <div
                                            key={hourIndex}
                                            className="box"
                                            onClick={() => handlePlannerClick(item, hour)}
                                        />
                                    ))}
                                </div>
                                {events
                                    .filter(event => event.itemId === item.id)
                                    .map((event) => {

                                        const { left, top, width } = calculateEventPosition(event, item, events);

                                        return (
                                            <div
                                                key={`E${event.id}`}
                                                className={`event ${event.done === 1 ? "done" : ""} ${selectedEventId === event.id ? "active" : ""}`}
                                                title={moment(event.startDate).format("DD.MM.") + " - " + moment(event.endDate).format("DD.MM.") + " " + (event.description ? event.description : event.statusName) + " " + event.customer}
                                                style={{ left: left, top: top, width: width, background: event.statusColor }}
                                                onClick={() => handleEventClick(event)}
                                            >
                                                <div className="leftBox" />
                                                <span className="desc">{(event.description ? event.description : event.statusName) + " " + event.customer}</span>
                                                <span>{moment(event.startDate).format("DD.MM.")} - {moment(event.endDate).format("DD.MM.")}</span>
                                                <div className="rightBox" />
                                            </div>
                                        );
                                    })}
                            </div>
                        ))}
                        {teams.map(item => (
                            <div className="row" key={`T${item.driver}`}>
                                <div className="boxes">
                                    {hours.map((hour, hourIndex) => (
                                        <div
                                            key={hourIndex}
                                            className="box"
                                            onClick={() => handlePlannerClick(item, hour)}
                                        />
                                    ))}
                                </div>
                                {events
                                    .filter(event => event.driver === item.driver)
                                    .map((event) => {

                                        const { left, top, width } = calculateEventPosition(event, item, events);

                                        return (
                                            <div
                                                key={`E${event.id}`}
                                                className={`event ${event.done === 1 ? "done" : ""} ${selectedEventId === event.id ? "active" : ""}`}
                                                title={moment(event.startDate).format("DD.MM.") + " - " + moment(event.endDate).format("DD.MM.") + " " + (event.description ? event.description : event.statusName) + " " + event.customer}
                                                style={{ left: left, top: top, width: width, background: event.statusColor }}
                                                onClick={() => handleEventClick(event)}
                                            >
                                                <div className="leftBox" />
                                                <span className="desc">{(event.description ? event.description : event.statusName) + " " + event.customer}</span>
                                                <span>{moment(event.startDate).format("DD.MM.")} - {moment(event.endDate).format("DD.MM.")}</span>
                                                <div className="rightBox" />
                                            </div>
                                        );
                                    })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {
                formModal && (
                    <ViewEvent
                        selectedEventId={selectedEventId}
                        setFormModal={setFormModal}
                        setSelectedEventId={setSelectedEventId}
                        setSelectedItem={selectedItem}
                        setSelectedTime={selectedTime}
                        fetchData={fetchData}
                    />
                )
            }
            
            <ViewDriver
                selectedDriverModal={selectedDriverModal}
                setSelectedDriverModal={setSelectedDriverModal}
                fetchData={fetchData}
            />
        </div >
    );
};

export default Calendar;
